import React, { createContext, useContext, useRef } from "react";
import { createStore, useStore } from "zustand";
import type { TimelinePanelStoreState } from "./types";
import { TimelinePanelMode } from "./types";

const createTimelinePanelStoreState = (
  initialState?: Partial<TimelinePanelStoreState>
): TimelinePanelStoreState => ({
  isOpen: initialState?.isOpen ?? false,
  drawerWidth: initialState?.drawerWidth ?? 0,
  targetDrawer: initialState?.targetDrawer ?? null,
  mode: initialState?.mode ?? TimelinePanelMode.OVERLAY,
  setIsOpen: () => undefined,
  setDrawerWidth: () => undefined,
  setTargetDrawer: () => undefined,
  setDrawerMode: () => undefined,
});

/**
 * Creates an isolated TimelinePanel Zustand store. Use with
 * `TimelinePanelStoreProvider` when a panel must not share open state with
 * other TimelinePanel instances on the page.
 */
export const createTimelinePanelStore = (
  initialState?: Partial<
    Pick<
      TimelinePanelStoreState,
      "isOpen" | "drawerWidth" | "targetDrawer" | "mode"
    >
  >
) => {
  const baseState = createTimelinePanelStoreState(initialState);

  return createStore<TimelinePanelStoreState>((set) => ({
    ...baseState,
    setIsOpen: (isOpen) => set({ isOpen }),
    setDrawerWidth: (drawerWidth) => set({ drawerWidth }),
    setTargetDrawer: (targetDrawer) => set({ targetDrawer }),
    setDrawerMode: (mode) => set({ mode }),
  }));
};

export type TimelinePanelStoreType = ReturnType<
  typeof createTimelinePanelStore
>;

/** Process-wide default store used by existing TimelinePanel consumers. */
export const defaultTimelinePanelStore = createTimelinePanelStore();

const TimelinePanelStoreContext = createContext<TimelinePanelStoreType | null>(
  null
);

export type TimelinePanelStoreProviderProps = {
  children: React.ReactNode;
  store?: TimelinePanelStoreType;
};

/**
 * Supplies a dedicated TimelinePanel store so drawers on the same page do not
 * fight over the global singleton.
 */
export const TimelinePanelStoreProvider = ({
  children,
  store: storeProp,
}: TimelinePanelStoreProviderProps) => {
  const storeRef = useRef<TimelinePanelStoreType>();

  if (!storeRef.current) {
    storeRef.current = storeProp ?? createTimelinePanelStore();
  }

  return (
    <TimelinePanelStoreContext.Provider value={storeRef.current}>
      {children}
    </TimelinePanelStoreContext.Provider>
  );
};

/**
 * Returns TimelinePanel store state from the nearest provider, or the default
 * singleton when no provider is mounted.
 */
export const useTimelinePanelStore = (): TimelinePanelStoreState => {
  const store =
    useContext(TimelinePanelStoreContext) ?? defaultTimelinePanelStore;

  return useStore(store);
};
